/**
 * Entrar, criar conta e escolher a escola.
 *
 * É a primeira tela que um coordenador vê, e provavelmente a única que ele vê
 * num dia em que só quer conferir um horário. Ela é curta de propósito: dois
 * campos, um botão, e o erro no lugar onde a pessoa está olhando.
 *
 * **O erro é o mesmo para senha errada e e-mail desconhecido.** Não é
 * desleixo de mensagem: distinguir os dois transformaria esta tela numa
 * consulta de "quem tem conta aqui", e numa rede pública isso é informação
 * sobre pessoas. A API já responde igual nos dois casos; aqui é só não
 * inventar diferença.
 *
 * **O projeto deixou de ser uma variável de build.** Antes o app era compilado
 * apontando para um projeto (`VITE_PROJETO`), o que só funcionava porque não
 * havia login. Agora a lista vem de quem entrou — e quem é membro de duas
 * escolas troca sem recompilar nada.
 */

import { useState } from 'react'

import { ErroApi, sessao, type ProjetoDoUsuario, type Usuario } from '../api'
import { Icon } from '../components/Icon'

type Modo = 'entrar' | 'criar'

/**
 * O papel em português.
 *
 * O enum do banco é ASCII e em caixa alta — `PROPRIETARIO` — porque é
 * identificador. Baixar a caixa dele dava "proprietario", sem acento, na cara
 * de quem entra. Identificador não vira texto de tela por `toLowerCase()`.
 */
const PAPEL: Record<string, string> = {
  PROPRIETARIO: 'proprietária',
  EDITOR: 'edita',
  LEITOR: 'só lê',
}

export function Entrar({ aoEntrar }: { aoEntrar: (u: Usuario) => void }) {
  const [modo, setModo] = useState<Modo>('entrar')
  const [email, setEmail] = useState('')
  const [senha, setSenha] = useState('')
  const [nome, setNome] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [ocupado, setOcupado] = useState(false)

  async function enviar(e: React.FormEvent) {
    e.preventDefault()
    setErro(null)
    setOcupado(true)
    try {
      if (modo === 'criar') await sessao.registrar(email, senha, nome)
      aoEntrar(await sessao.entrar(email, senha))
    } catch (x) {
      setErro(x instanceof ErroApi ? x.message : 'não foi possível entrar agora')
    } finally {
      setOcupado(false)
    }
  }

  return (
    <main className="entrada">
      <form className="entrada__caixa" onSubmit={enviar}>
        <h1 className="entrada__marca">Pauta</h1>
        <p className="entrada__nota">
          {modo === 'entrar' ? 'Montagem de grade horária escolar.' : 'Criar uma conta nova.'}
        </p>

        {modo === 'criar' && (
          <label className="field">
            <span className="field__label">Nome</span>
            <input
              className="input"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              autoComplete="name"
            />
          </label>
        )}

        <label className="field">
          <span className="field__label">E-mail</span>
          <input
            className="input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
            required
          />
        </label>

        <label className="field">
          <span className="field__label">Senha</span>
          <input
            className="input"
            type="password"
            value={senha}
            onChange={(e) => setSenha(e.target.value)}
            autoComplete={modo === 'criar' ? 'new-password' : 'current-password'}
            required
          />
          {modo === 'criar' && (
            <span className="field__hint">
              Pelo menos 10 caracteres. Uma frase que só você saiba vale mais que
              trocar letra por símbolo.
            </span>
          )}
        </label>

        {erro && (
          <p className="state state--pendencia" role="alert">
            <Icon name="aviso" size={14} />
            {erro}
          </p>
        )}

        <button type="submit" className="btn btn--primary" disabled={ocupado}>
          {ocupado ? 'Um momento…' : modo === 'entrar' ? 'Entrar' : 'Criar conta'}
        </button>

        <button
          type="button"
          className="btn btn--ghost"
          onClick={() => {
            setModo(modo === 'entrar' ? 'criar' : 'entrar')
            setErro(null)
          }}
        >
          {modo === 'entrar' ? 'Criar uma conta' : 'Já tenho conta'}
        </button>
      </form>
    </main>
  )
}

/**
 * Escolher a escola.
 *
 * Aparece quando a pessoa é membro de mais de um projeto, e some quando é de
 * um só — nesse caso a escolha é feita por ela. Uma tela que existe para
 * confirmar o óbvio é uma tela a menos que o produto precisa ter.
 */
export function EscolherProjeto({
  projetos,
  aoEscolher,
  aoCriar,
}: {
  projetos: ProjetoDoUsuario[]
  aoEscolher: (id: string) => void
  aoCriar: (nome: string, ano: number) => Promise<void>
}) {
  const [nome, setNome] = useState('')
  const [ano, setAno] = useState(new Date().getFullYear() + 1)
  const [erro, setErro] = useState<string | null>(null)

  return (
    <main className="entrada">
      <div className="entrada__caixa">
        <h1 className="entrada__marca">Pauta</h1>

        {projetos.length > 0 ? (
          <>
            <p className="entrada__nota">Em qual escola você vai trabalhar?</p>
            <ul className="ruled">
              {projetos.map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    className="btn btn--ghost entrada__projeto"
                    onClick={() => aoEscolher(p.id)}
                  >
                    <span className="ruled__val">{p.nome}</span>
                    <span className="ruled__note">
                      {p.ano_letivo} · {PAPEL[p.papel] ?? p.papel}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </>
        ) : (
          <p className="entrada__nota">
            Você ainda não participa de nenhuma escola. Crie a primeira, ou peça a
            quem administra a sua para convidar este e-mail.
          </p>
        )}

        <h2 className="sub-head">Criar uma escola</h2>
        <form
          className="entrada__criar"
          onSubmit={async (e) => {
            e.preventDefault()
            setErro(null)
            try {
              await aoCriar(nome.trim(), ano)
            } catch (x) {
              setErro(x instanceof ErroApi ? x.message : 'não foi possível criar agora')
            }
          }}
        >
          <label className="field">
            <span className="field__label">Nome</span>
            <input
              className="input"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              required
            />
          </label>
          <label className="field">
            <span className="field__label">Ano letivo</span>
            <input
              className="input"
              type="number"
              value={ano}
              onChange={(e) => setAno(Number(e.target.value))}
              required
            />
          </label>
          {erro && (
            <p className="state state--pendencia" role="alert">
              <Icon name="aviso" size={14} />
              {erro}
            </p>
          )}
          <button type="submit" className="btn btn--primary">
            Criar
          </button>
        </form>
      </div>
    </main>
  )
}
